"use client";

import React from "react";
import Image from "next/image";
import { fontWeights } from "@/utils/fonts";
import { Button } from "../ui/button";

interface RecentWorkItem {
    logo: string;
    name: string;
    description1: string;
    description2: string;
    websiteLink: string;
    playStoreLink?: string;
    mainImage: string;
    demoGif?: string;
    nameColor?: string;
    websiteBtnImage?: string;
    playStoreBtnImage?: string;
}

interface RecentWorkProps {
    title?: string;
    items?: RecentWorkItem[];
    locationName?: string;
    serviceName?: string;
}

const clickHandler = () => {
    window.location.href = "/portfolio";
};

export function RecentWorks({ title, items, locationName, serviceName }: RecentWorkProps) {
    const replaceDynamicPlaceholders = (text: string | undefined) => {
        if (!text) return "";
        return text
            .replace(/{location}/g, locationName || "")
            .replace(/{service}/g, serviceName || "");
    };

    const displayItems = items && items.length > 0 ? items : [];
    const finalTitle = replaceDynamicPlaceholders(title);

    return (
        <section className="py-[2rem] xl:py-[24px] lg:py-[12px] sm:py-[4px]">
            <div className="max-w-[1313px] mx-auto">
                <h2
                    className="
            text-[24px] 
            lg:text-[35px] 
            2xl:text-[40px] 
            3xl:text-[45px] 
            text-center 
            mb-[20px]
            md:mb-[50px]
          "
                    style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.semiBold }}
                    dangerouslySetInnerHTML={{ __html: finalTitle }}
                />
                <div className="flex flex-col gap-y-0 sm:gap-y-[40px] md:gap-y-[60px] lg:gap-y-[100px] xl:gap-y-[120px] 2xl:gap-y-[150px] mx-4 sm:mx-6">
                    {displayItems.map((item, index) => {
                        const isEven = index % 2 === 0;
                        if (isEven) {
                            return (
                                <React.Fragment key={index}>
                                    <div className="flex-row sm:flex items-center justify-center gap-0  max-w-auto sm:max-w-[600px] md:max-w-[710px] lg:max-w-[980px] xl:max-w-[1100px] 2xl:max-w-[1390px] ml-auto mr-auto max-w-auto">
                                        <div className="flex-1">
                                            <div className="flex flex-col tsm-350:flex-row mb-[20px] md:mb-[40px] items-center justify-start gap-2 sm:gap-4 mx-auto ">
                                                {item.logo && <Image src={item.logo} width={150} height={150} alt={item.name} className="w-[40px] h-[40px] sm:w-[50px] sm:h-[50px] md:w-[70px] md:h-[70px] lg:w-[90px] lg:h-[90px]" />}
                                                <h3
                                                    className="
                                                    text-[24px]
                                                    lg:text-[35px]
                                                    2xl:text-[40px]
                                                    3xl:text-[45px]
                                                    text-[#000000]
                                                    text-center
                                                    tracking-widest
                                                    "
                                                    style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.bold, color: item.nameColor }}
                                                >
                                                    {replaceDynamicPlaceholders(item.name)}
                                                </h3>
                                            </div>
                                            <p
                                                className="
                                                text-[#000000] 
                                                mb-2  
                                                sm:mb-4  
                                                lg:mb-6  
                                                text-[14px]
                                                lg:text-[20px]
                                                leading-6
                                                "
                                                style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.regular }}
                                            >
                                                {replaceDynamicPlaceholders(item.description1)}
                                            </p>
                                            <p
                                                className="
                                                text-[#000000] 
                                                text-[14px]
                                                lg:text-[20px]
                                                leading-7
                                                "
                                                style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.regular }}
                                            >
                                                {replaceDynamicPlaceholders(item.description2)}
                                            </p>
                                            <div className="flex gap-10 lg:gap-10 pt-[10px] sm:pt-[40px] mx-auto xl:mx-0 justify-center xl:justify-start -space-x-[80px] tsm-400:-space-x-[20px] sm:-space-x-[40px] lg:-space-x-4">
                                                {item.websiteLink && item.websiteBtnImage && (
                                                    <a href={item.websiteLink} target="_blank" rel="noopener noreferrer" aria-label={`Visit ${item.name} website`}>
                                                        <div className="cursor-pointer">
                                                            <Image src={item.websiteBtnImage} width={200} height={50} alt="Visit Website" className="lg:scale-100 sm:scale-[0.8] scale-[0.6]" />
                                                        </div>
                                                    </a>
                                                )}
                                                {item.playStoreLink && item.playStoreBtnImage && (
                                                    <a href={item.playStoreLink} target="_blank" rel="noopener noreferrer" aria-label={`Download ${item.name} on Play Store`}>
                                                        <div className="cursor-pointer">
                                                            <Image src={item.playStoreBtnImage} width={200} height={50} alt="Download on Play Store" className="lg:scale-100 sm:scale-[0.8] scale-[0.6]" />
                                                        </div>
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex-1 flex items-center justify-center relative mx-auto">
                                            {item.demoGif && (
                                                <Image
                                                    src={item.demoGif}
                                                    alt={`${item.name} Demo`}
                                                    width={207}
                                                    height={423}
                                                    unoptimized
                                                    sizes="(max-width: 768px) 100vw, 30vw"
                                                    className="
                                                    relative
                                                    sm:absolute
                                                    p-5
                                                    tsm-350:p-0
                                                    scale-auto
                                                    tsm-350:scale-[0.7]
                                                    sm:scale-[0.6]
                                                    lg:scale-90
                                                    2xl:scale-100               
                                                    "
                                                />
                                            )}
                                        </div>
                                        <div className="flex-1 relative mx-auto max-h-auto tsm-350:max-h-[200px] sm:max-h-[380px] max-w-auto tsm-350:max-w-[318px] sm:max-w-[634px] overflow-hidden flex items-center justify-center">
                                            {item.mainImage && (
                                                <Image
                                                    src={item.mainImage}
                                                    alt={`${item.name} Interface`}
                                                    width={2400}
                                                    height={1600}
                                                    unoptimized
                                                    sizes="(max-width: 768px) 100vw, 50vw"
                                                    className="
                                                    origin-top-left 
                                                    w-full
                                                    h-auto
                                                    tsm-350:scale-auto
                                                    sm:scale-100
                                                    lg:scale-100
                                                    2xl:scale-100
                                                    mt-0
                                                    sm:mt-6
                                                    md:mt-12
                                                    lg:mt-20
                                                    2xl:mt-0
                                                    mx-auto 
                                                    sm:mx-0
                                                    md:ml-2
                                                    lg:ml-0
                                                    "
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="pb-[40px] bg-white block sm:hidden">
                                        <div className="lg:h-[1px] h-[0.95px] lg:w-[200px] w-[150px] bg-[#002F6D] mx-auto" />
                                    </div>
                                </React.Fragment>
                            );
                        } else {
                            // Odd index (Mydryfruit layout)
                            return (
                                <React.Fragment key={index}>
                                    <div className="flex-row-reverse sm:flex items-center gap-0 sm:gap-[40px] md:gap-[2rem] xl:gap-[3rem] 2xl:gap-[5rem] max-w-auto sm:max-w-[600px] md:max-w-[710px] lg:max-w-[980px] xl:max-w-[1100px] 2xl:max-w-[1390px] ml-0 sm:ml-auto mr-0 sm:mr-auto">
                                        <div className="flex-1 relative h-full max-w-[634px]">
                                            <div className="flex flex-col tsm-350:flex-row mb-[20px] md:mb-[40px] items-center justify-start gap-2 sm:gap-4 mx-auto ">
                                                {item.logo && <Image src={item.logo} width={150} height={150} alt={item.name} className="w-[40px] h-[40px] sm:w-[50px] sm:h-[50px] md:w-[70px] md:h-[70px] lg:w-[90px] lg:h-[90px]" />}
                                                <h3
                                                    className="
                                                    text-[24px]
                                                    lg:text-[35px]
                                                    2xl:text-[40px]
                                                    3xl:text-[45px]
                                                    text-[#1a7894]
                                                    text-center
                                                    tracking-wide
                                                    "
                                                    style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.bold, color: item.nameColor }}
                                                >
                                                    {replaceDynamicPlaceholders(item.name)}
                                                </h3>
                                            </div>
                                            <p
                                                className="
                                                text-[#000000] 
                                                mb-2  
                                                sm:mb-4 
                                                lg:mb-6
                                                text-[14px]
                                                lg:text-[20px]
                                                max-w-[560px]
                                                leading-6
                                                "
                                                style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.regular }}
                                            >
                                                {replaceDynamicPlaceholders(item.description1)}
                                            </p>
                                            <p
                                                className="
                                                text-[#000000]
                                                mb-0
                                                sm:mb-4
                                                lg:mb-6
                                                xl:mb-8
                                                2xl:mb-10  
                                                text-[14px]
                                                lg:text-[20px]
                                                max-w-[560px]
                                                leading-6
                                                "
                                                style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.regular }}
                                            >
                                                {replaceDynamicPlaceholders(item.description2)}
                                            </p>
                                            {item.websiteLink && item.websiteBtnImage && (
                                                <a href={item.websiteLink} target="_blank" rel="noopener noreferrer" aria-label={`Visit ${item.name} website`}>
                                                    <div className="cursor-pointer flex mt-4">
                                                        <Image alt="Visit Website" width={200} height={50} src={item.websiteBtnImage} className="lg:scale-100 sm:scale-[0.8] scale-[0.6]" />
                                                    </div>
                                                </a>
                                            )}
                                        </div>
                                        <div className="flex-1 relative mx-auto max-h-auto tsm-350:max-h-[200px] sm:max-h-[380px] max-w-auto tsm-350:max-w-[318px] sm:max-w-[634px] overflow-hidden">
                                            {item.mainImage && (
                                                <Image
                                                    src={item.mainImage}
                                                    alt={`${item.name} Interface`}
                                                    width={634}
                                                    height={399}
                                                    unoptimized
                                                    sizes="(max-width: 768px) 100vw, 50vw"
                                                    className="
                                                    origin-top-left 
                                                    h-auto
                                                    tsm-350:scale-auto
                                                    sm:scale-[0.85]
                                                    lg:scale-90
                                                    2xl:scale-100
                                                    mt-0
                                                    sm:mt-6
                                                    md:mt-12
                                                    lg:mt-20
                                                    2xl:mt-0
                                                    mx-auto 
                                                    sm:mx-0
                                                    md:ml-2
                                                    lg:ml-0
                                                    "
                                                />
                                            )}
                                        </div>
                                    </div>
                                    {index < displayItems.length - 1 && (
                                        <div className="pb-[40px] bg-white block sm:hidden">
                                            <div className="lg:h-[1px] h-[0.95px] lg:w-[200px] w-[150px] bg-[#002F6D] mx-auto" />
                                        </div>
                                    )}
                                </React.Fragment>
                            );
                        }
                    })}
                </div>
            </div>
            <div
                className="
          pt-[20px]
          sm:pt-[40px]
          lg:pt-[60px]
          xl:pt-[80px]
          2xl:pt-[100px]    
          pb-[30px]
          sm:pb-[40px]
          lg:pb-[50px]
          xl:pb-[55px]
          2xl:pb-[60px]    
          bg-white
          flex
          flex-col
          items-center
          justify-center
        "
            >
                <Button
                    variant="outline"
                    aria-label="See more recent works"
                    className="
          bg-[#002F6D]
          text-white 
          hover:bg-[#002F6D]/90 
          w-[110px]
          h-[40px]
          lg:w-[150px] 
          lg:h-[50px]
          rounded-[8px]
          sm:rounded-[10px]
          text-[15px] 
          3xl:text-[18px]
          hover:text-orange-300
          "
                    style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.regular }}
                    onClick={clickHandler}
                >
                    See More
                </Button>
                <div className="lg:h-[1px] h-[0.95px] lg:w-[200px] w-[150px] bg-[#002F6D] mx-auto mt-10" />
            </div>
        </section>
    );
}
